import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Download, Mail, Loader2, CheckCircle2, Check, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { submitInquiry } from "@/lib/api";
import CTAButtons from "@/components/CTAButtons";

const API_URL = process.env.REACT_APP_BACKEND_URL;

function collectTracking() {
    return {
        user_agent: navigator.userAgent || null,
        referrer: document.referrer || null,
        page_url: window.location.href || null,
        screen: window.screen ? `${window.screen.width}x${window.screen.height}` : null,
        language: navigator.language || null,
        timezone: Intl?.DateTimeFormat()?.resolvedOptions()?.timeZone || null,
        platform: navigator.userAgentData?.platform || navigator.platform || null,
        client_timestamp: new Date().toISOString(),
    };
}

function fireChecklistTrack(checklist_type) {
    fetch(`${API_URL}/api/track/checklist`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ checklist_type, ...collectTracking() }),
    }).catch(() => {});
}

function fireChecklistLeadTrack(name, email, checklist_type) {
    fetch(`${API_URL}/api/track/checklist-lead`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, email, checklist_type, ...collectTracking() }),
    }).catch(() => {});
}

const VAT_INVOICE_PDF =
    "/assets/sars-vat-invoice-checklist.pdf";

const REQUIREMENTS = [
    "The words 'Tax Invoice', 'VAT Invoice' or 'Invoice'",
    "Supplier's name, trading name and address",
    "Supplier's VAT registration number",
    "Customer's name (and address for invoices > R5,000)",
    "Customer's VAT registration number (where applicable)",
    "Serialised invoice number",
    "Date of issue",
    "Description of the goods or services supplied",
    "Quantity or volume supplied",
    "Value of the supply, the VAT charged and the consideration including VAT",
    "Where zero-rated, the words 'zero-rated' alongside the line",
    "Where exempt, the words 'exempt supply'",
];

export default function VATInvoiceChecklist() {
    const handleAnonymousDownload = () => {
        fireChecklistTrack("vat-invoice");
    };

    return (
        <div className="bg-tca-fog min-h-screen" data-testid="page-checklist-vat-invoice">
            {/* Toolbar */}
            <div className="bg-white border-b border-tca-line">
                <div className="container-tca py-4 flex items-center justify-between gap-3">
                    <Link to="/checklists" className="btn-ghost text-base gap-2.5" data-testid="back-to-checklists">
                        <ArrowLeft className="w-5 h-5" /> All checklists
                    </Link>
                    <a
                        href={VAT_INVOICE_PDF}
                        target="_blank"
                        rel="noopener noreferrer"
                        download
                        onClick={handleAnonymousDownload}
                        data-testid="vat-invoice-direct-download"
                        className="btn-secondary"
                    >
                        <Download className="w-4 h-4" /> Download PDF
                    </a>
                </div>
            </div>

            {/* Hero */}
            <section className="bg-white border-b border-tca-line">
                <div className="container-tca py-16 md:py-20 grid grid-cols-1 lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-7">
                        <p className="overline text-tca-orange mb-4">VAT Compliance · Section 20 of the VAT Act</p>
                        <h1 className="font-display font-extrabold text-tca-ink text-4xl sm:text-5xl lg:text-6xl leading-[1.02]">
                            SARS VAT Tax Invoice Checklist
                        </h1>
                        <p className="mt-5 text-tca-mute text-lg leading-relaxed max-w-2xl">
                            Every requirement of a valid SARS Tax Invoice on one page. Pin it next to your invoice template — it is the single easiest way to avoid disallowed input VAT during a SARS verification or audit.
                        </p>
                    </div>
                    <div className="lg:col-span-5">
                        <EmailCaptureCard />
                    </div>
                </div>
            </section>

            {/* What's inside */}
            <section className="bg-tca-fog">
                <div className="container-tca py-16">
                    <div className="max-w-3xl">
                        <p className="overline mb-3">A quick preview</p>
                        <h2 className="font-display font-extrabold text-tca-ink text-3xl sm:text-4xl leading-tight">
                            What a valid Tax Invoice must contain.
                        </h2>
                    </div>
                    <ul className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-px bg-tca-line border border-tca-line max-w-4xl">
                        {REQUIREMENTS.map((r, i) => (
                            <li
                                key={i}
                                data-testid={`vat-requirement-${i}`}
                                className="bg-white px-5 py-4 flex items-start gap-3 text-sm text-tca-ink leading-snug"
                            >
                                <Check className="w-4 h-4 mt-0.5 text-tca-orange shrink-0" />
                                <span>{r}</span>
                            </li>
                        ))}
                    </ul>
                    <p className="mt-6 text-xs text-tca-mute italic max-w-3xl">
                        Above R5,000 you must include the customer's full address. Abridged invoices (under R5,000) may omit address details. SARS strictly enforces these requirements.
                    </p>
                </div>
            </section>

            {/* CTA */}
            <section className="bg-white border-t border-tca-line">
                <div className="container-tca py-14 md:py-16 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
                    <div>
                        <h2 className="font-display font-extrabold text-2xl sm:text-3xl text-tca-ink leading-[1.1]">Want us to lock down your invoice template?</h2>
                        <p className="mt-3 text-tca-mute">We review every client's invoicing template against this checklist as part of monthly VAT cycle work.</p>
                    </div>
                    <CTAButtons testIdPrefix="vat-invoice-cta" />
                </div>
            </section>
        </div>
    );
}

function EmailCaptureCard() {
    const [form, setForm] = useState({ name: "", email: "", company: "" });
    const [status, setStatus] = useState("idle");
    const [errors, setErrors] = useState({});

    const update = (k) => (e) => {
        setForm((f) => ({ ...f, [k]: e.target.value }));
        if (errors[k]) setErrors((er) => ({ ...er, [k]: undefined }));
    };

    const EMAIL_RE = /^[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}$/;
    const handleEmailChange = (e) => {
        const val = e.target.value;
        setForm((f) => ({ ...f, email: val }));
        if (val.length === 0) {
            setErrors((er) => ({ ...er, email: undefined }));
        } else if (!EMAIL_RE.test(val.trim())) {
            setErrors((er) => ({ ...er, email: "Please enter a valid email address (e.g. name@example.co.za)." }));
        } else {
            setErrors((er) => ({ ...er, email: undefined }));
        }
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        const er = {};
        if (form.name.trim().length < 2) er.name = "Please enter your name.";
        if (!EMAIL_RE.test(form.email.trim())) er.email = "Please enter a valid email address (e.g. name@example.co.za).";
        if (Object.keys(er).length) {
            setErrors(er);
            return;
        }
        setStatus("loading");
        try {
            await submitInquiry({
                name: form.name,
                email: form.email,
                company: form.company || null,
                subject: "SARS VAT Invoice Checklist requested",
                message: `SARS VAT Invoice Checklist requested via website. Please send a copy to ${form.email}.`,
                source: "checklist-vat-invoice",
            });
            // Fire-and-forget admin tracking notification
            fireChecklistLeadTrack(form.name, form.email, "vat-invoice");
            setStatus("success");
            toast.success("Sent! Check your inbox shortly.");
        } catch (err) {
            console.error(err);
            setStatus("idle");
            toast.error("Could not send. Please try again or download directly.");
        }
    };

    return (
        <div
            className="border border-tca-orange bg-tca-orangeSoft/40 p-6 lg:p-7"
            data-testid="vat-invoice-email-capture"
        >
            <div className="flex items-center gap-2 mb-2">
                <Mail className="w-4 h-4 text-tca-orange" />
                <p className="overline text-tca-orange">Free · Emailed instantly</p>
            </div>
            <h3 className="font-display font-extrabold text-tca-ink text-xl leading-tight mb-2">
                Email me the SARS VAT Invoice Checklist
            </h3>

            {status === "success" ? (
                <div className="bg-white border border-tca-line p-4 mt-4 flex items-start gap-3" data-testid="vat-capture-success">
                    <CheckCircle2 className="w-5 h-5 text-tca-orange shrink-0 mt-0.5" />
                    <div className="flex-1">
                        <p className="font-display font-bold text-tca-ink text-sm mb-1">Sent to {form.email}</p>
                        <p className="text-xs text-tca-mute leading-relaxed">
                            Check your inbox in a minute. You can also download the PDF directly below.
                        </p>
                        <a
                            href={VAT_INVOICE_PDF}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="vat-success-download"
                            className="btn-ghost mt-3 inline-flex"
                        >
                            <ExternalLink className="w-3.5 h-3.5" /> Open PDF now
                        </a>
                    </div>
                </div>
            ) : (
                <form onSubmit={onSubmit} className="space-y-3" data-testid="vat-capture-form" noValidate>
                    <div>
                        <input
                            type="text"
                            value={form.name}
                            onChange={update("name")}
                            placeholder="Your name"
                            data-testid="vat-capture-name"
                            aria-label="Your name"
                            className="w-full border border-tca-line bg-white px-4 py-3 text-sm focus:outline-none focus:border-tca-orange focus:ring-2 focus:ring-tca-orangeSoft"
                        />
                        {errors.name && <p className="text-[11px] text-red-600 mt-1">{errors.name}</p>}
                    </div>
                    <div>
                        <input
                            type="email"
                            value={form.email}
                            onChange={handleEmailChange}
                            placeholder="you@company.co.za"
                            data-testid="vat-capture-email"
                            aria-label="Your email"
                            className={`w-full border px-4 py-3 text-sm focus:outline-none focus:ring-2 bg-white transition-colors ${
                                errors.email
                                    ? "border-red-500 focus:border-red-500 focus:ring-red-200"
                                    : "border-tca-line focus:border-tca-orange focus:ring-tca-orangeSoft"
                            }`}
                        />
                        {errors.email && <p className="text-[11px] text-red-600 mt-1">{errors.email}</p>}
                    </div>
                    <div>
                        <input
                            type="text"
                            value={form.company}
                            onChange={update("company")}
                            placeholder="Company (optional)"
                            data-testid="vat-capture-company"
                            aria-label="Company"
                            className="w-full border border-tca-line bg-white px-4 py-3 text-sm focus:outline-none focus:border-tca-orange focus:ring-2 focus:ring-tca-orangeSoft"
                        />
                    </div>
                    <button
                        type="submit"
                        disabled={status === "loading"}
                        data-testid="vat-capture-submit"
                        className="btn-primary w-full disabled:opacity-60"
                    >
                        {status === "loading" ? (
                            <>
                                <Loader2 className="w-4 h-4 animate-spin" /> Sending...
                            </>
                        ) : (
                            <>
                                <Mail className="w-4 h-4" /> Send to my inbox
                            </>
                        )}
                    </button>
                    <p className="text-[11px] text-tca-mute leading-relaxed pt-1">
                        Or skip the inbox — there's a direct download in the toolbar above.
                    </p>
                </form>
            )}
        </div>
    );
}
